import { useTranslation } from "react-i18next";
import { Bot, Check, MessageSquare, Pencil, Undo2, X } from "lucide-react";

import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { formatDateBR } from "@/shared/utils/datetime";
import type { Learning } from "../types/LearningTypes";
import { actionKey, awaitingKey, destinationKey, isEditable, lineDiff, showsBefore } from "../utils/learningsView";

interface LearningCardProps {
  learning: Learning;
  busy: boolean;
  onAccept: () => void;
  onEdit: () => void;
  onReject: () => void;
  onRemove: () => void;
  onReferences: () => void;
}

/**
 * Um aprendizado (AB#87286): o que mudou, onde, por quê e de onde veio. Na Memory ele já foi
 * aplicado e pode ser removido; na Mind é uma sugestão que um admin aplica ou descarta.
 */
export const LearningCard = ({ learning, busy, onAccept, onEdit, onReject, onRemove, onReferences }: LearningCardProps) => {
  const { t } = useTranslation();
  const awaiting = awaitingKey(learning);
  const decide = learning.status === "pending" && learning.can_decide;

  return (
    <li className="space-y-3 rounded-md border p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary">{t(destinationKey(learning))}</Badge>
        <span className="text-sm font-medium">{t(actionKey(learning))}</span>
        {learning.mind_title && <span className="text-sm text-muted-foreground">· {learning.mind_title}</span>}
        {learning.kind && learning.target === "memory" && (
          <Badge variant="outline" className="text-[10px]">
            {t(`memory.kinds.${learning.kind}`)}
          </Badge>
        )}
        <Badge variant="outline" className="text-[10px]">
          {t(`learnings.signal.${learning.signal}`)}
        </Badge>
        {awaiting && (
          <Badge variant={learning.can_decide ? "warning" : "outline"} className="ml-auto text-[10px]">
            {t(awaiting)}
          </Badge>
        )}
      </div>

      {showsBefore(learning) && learning.target === "mind" && learning.content ? (
        <pre className="max-h-64 overflow-auto rounded-md bg-muted/40 p-3 text-xs leading-relaxed">
          {lineDiff(learning.before_content!, learning.content).map((line, index) => (
            <div
              key={`${index}-${line.kind}`}
              className={
                line.kind === "added"
                  ? "bg-green-500/10 text-green-700 dark:text-green-400"
                  : line.kind === "removed"
                    ? "bg-red-500/10 text-red-700 line-through dark:text-red-400"
                    : "text-muted-foreground"
              }
            >
              {line.kind === "added" ? "+ " : line.kind === "removed" ? "- " : "  "}
              {line.text}
            </div>
          ))}
        </pre>
      ) : (
        <>
          {showsBefore(learning) && (
            <blockquote className="whitespace-pre-wrap border-l-2 pl-3 text-xs text-muted-foreground line-through">
              {learning.before_content}
            </blockquote>
          )}
          {learning.content && <p className="whitespace-pre-wrap text-sm">{learning.content}</p>}
        </>
      )}

      <div className="space-y-1 text-xs text-muted-foreground">
        <p>
          <span className="font-medium text-foreground">{t("learnings.why")}</span> {learning.rationale}
        </p>
        {learning.cause && (
          <p>
            <span className="font-medium text-foreground">{t("learnings.cause")}</span> {learning.cause}
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        {learning.agent && (
          <span className="flex items-center gap-1">
            <Bot className="h-3 w-3" />
            {t("learnings.fromAgent", { client: learning.agent.client ?? t("learnings.externalAgent") })}
          </span>
        )}
        {learning.origin === "conversation" && (
          <span className="flex items-center gap-1">
            <Bot className="h-3 w-3" />
            {t("learnings.fromConversation")}
          </span>
        )}
        {learning.from_me && <span>{t("learnings.fromYou")}</span>}
        <span>{learning.created_at ? formatDateBR(learning.created_at) : ""}</span>
        {learning.reference_count > 0 && (
          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={onReferences}>
            <MessageSquare className="mr-1 h-3 w-3" />
            {t("learnings.referencesButton", { count: learning.reference_count })}
          </Button>
        )}
        {decide && (
          <div className="ml-auto flex items-center gap-1">
            <Button size="sm" variant="ghost" disabled={busy} onClick={onReject}>
              <X className="mr-1 h-4 w-4" />
              {t("learnings.reject")}
            </Button>
            {isEditable(learning) && (
              <Button size="sm" variant="outline" disabled={busy} onClick={onEdit}>
                <Pencil className="mr-1 h-4 w-4" />
                {t("learnings.editAccept")}
              </Button>
            )}
            <Button size="sm" disabled={busy} onClick={onAccept}>
              <Check className="mr-1 h-4 w-4" />
              {t("learnings.accept")}
            </Button>
          </div>
        )}
        {learning.status === "accepted" && learning.can_remove && (
          <Button size="sm" variant="ghost" className="ml-auto" disabled={busy} onClick={onRemove}>
            <Undo2 className="mr-1 h-4 w-4" />
            {t("learnings.remove")}
          </Button>
        )}
      </div>
    </li>
  );
};
